import React, { useCallback, useEffect, useState } from "react";
import {
  clone,
  filter,
  get,
  head,
  includes,
  isEmpty,
  map,
  size,
  union,
  without,
} from "lodash";
import { showError, showSuccess } from "../v2_actions/error";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import DeleteIcon from "@material-ui/icons/Delete";
import Gallery from "react-photo-gallery";
import { GalleryPhotograph } from "./GalleryPhotograph";
import LoadingSpinner from "components/LoadingSpinner";
import { PageLayout } from "./layout/PageLayout";
import { PhotographEditor } from "./PhotographEditor";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import { confirmModal } from "../v2_actions/ui";
import { galleryList } from "../v2_actions/gallery";
import { getUserStoredCountry } from "../v2_actions/auth_country";
import { makeStyles } from "@material-ui/core";
import { photographList } from "../v2_actions/photograph";

const ROW_HEIGHT = "200px";

const useStyles = makeStyles({
  photographs_container: {
    display: "flex",
    flexWrap: "wrap",
    overflowY: "scroll",
    border: "solid 1px",
    width: "100%",
  },
  photograph_container: {
    margin: "10px",
    position: "relative",
    height: ROW_HEIGHT,
    backgroundColor: "lightgray",
  },
  photograph: {
    height: ROW_HEIGHT,
  },
  gallerySpecificAction: {
    display: "flex",
    alignItems: "center",
  },
});

export const GalleryView = ({ match }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { gallery_id } = useParams();
  const searchParams = window.location.search;
  const urlParams = new URLSearchParams(searchParams);
  const isUpdated = urlParams.get("updated");
  const country = getUserStoredCountry();
  const gallery = useSelector(() => galleryList.getObject(gallery_id));
  const loadingGalleries = useSelector(() => galleryList.isLoading());
  const galleries = useSelector(() => galleryList.getVisibleObjects());
  const isLoading = galleryList.isLoading();
  const [firstGalleryId, setFirstGalleryId] = useState(null);
  const [defaultGalleryUrl, setDefaultGalleryUrl] = useState(false);
  const [activePhotographId, setActivePhotographId] = useState(null);
  const [selectedPhotographIds, setSelectedPhotographIds] = useState([]);
  const firstGallery = useSelector(() => galleryList.getObject(firstGalleryId));
  const galleryPhotographs = useSelector(() =>
    photographList.getVisibleObjects(),
  );
  const [editingPhotographId, setEditingPhotographId] = useState(null);

  useEffect(() => {
    if (isUpdated) dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
  }, [isUpdated]);

  useEffect(() => {
    /* when this page is reached via dashboardRoutes
     * path is set to /photographer/gallery/:id/view
     * fetching with :id causes an exception on the api */
    if (gallery_id && gallery_id !== ":id") {
      dispatch(photographList.fetchGalleryPhotographs(gallery_id));
    }
  }, [isUpdated, gallery_id]);

  useEffect(() => {
    dispatch(galleryList.ensureObjectLoaded(gallery_id));
  }, [gallery_id]);

  useEffect(() => {
    /* when this page is reached via dashboardRoutes
     * path is set to /photographer/gallery/:id/view
     * get first visible gallery and reload page */
    if (includes(gallery_id, ":id")) {
      dispatch(galleryList.fetchListIfNeeded());
      setFirstGalleryId(get(head(galleries), "id"));
      dispatch(galleryList.ensureObjectLoaded(gallery_id));
    }
  }, [gallery_id, galleries, firstGallery]);

  useEffect(() => {
    if (!defaultGalleryUrl && !isEmpty(firstGallery)) {
      history.push(`/photographer/gallery/${get(firstGallery, "id")}/view`);
      setDefaultGalleryUrl(true);
    }
  }, [firstGallery]);

  const onDeletePhotograph = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", "Removed from the gallery");
      }
    };

    const onConfirmed = () => {
      const values = Object.assign({}, photograph, {
        available_to_purchase: false,
      });
      dispatch(photographList.saveObject(values)).then(onDeleted);
    };

    dispatch(
      confirmModal({
        text: "Are you sure you want to make this photograph unavailable to purchase?",
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onDeletePhotographWithoutGallerySizeFileInfo = (photograph) => {
    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(photographList.fetchGalleryPhotographs(gallery_id));
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Removed", `Photo: ${photograph.photo_name}`);
      }
    };
    //dispatch(photographList.deleteObject(photograph.id)).then(onDeleted);
  };

  const onDeleteSelectedPhotographs = () => {
    let photograph_ids = clone(selectedPhotographIds);
    const num_photographs_to_delete = size(photograph_ids);

    const onDeleted = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
        return;
      }

      photograph_ids = without(photograph_ids, json.id);
      if (size(photograph_ids) > 0) {
        deleteNext();
        return;
      }

      dispatch(galleryList.invalidateList());
      dispatch(galleryList.fetchListIfNeeded());
      showSuccess(
        "Removed",
        `Removed ${num_photographs_to_delete} photograph${
          num_photographs_to_delete > 1 ? "s" : ""
        } from the gallery`,
      );
    };

    const deleteNext = () => {
      const photograph_id = head(photograph_ids);
      const photograph = head(
        filter(
          gallery.photographs,
          (photograph) => photograph.id === photograph_id,
        ),
      );
      const values = Object.assign({}, photograph, {
        available_to_purchase: false,
      });
      dispatch(photographList.saveObject(values)).then(onDeleted);
    };

    const onConfirmed = () => {
      deleteNext();
    };

    dispatch(
      confirmModal({
        text: `Are you sure you want make ${size(
          photograph_ids,
        )} photographs unavailable for purchase?`,
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onSetAsMapPhotograph = (photograph) => {
    const onSaved = (json) => {
      if (json.errors) {
        showError("Error", JSON.stringify(json.errors));
      } else {
        dispatch(galleryList.invalidateList());
        dispatch(galleryList.fetchListIfNeeded());
        showSuccess("Set", "Set as map photograph");
      }
    };

    const onConfirmed = () => {
      const values = Object.assign({}, gallery, {
        map_photograph: photograph.id,
      });
      dispatch(galleryList.saveObject(values)).then(onSaved);
    };

    dispatch(
      confirmModal({
        text: "Are you sure you want to make this the map photograph?",
        onConfirmed: onConfirmed,
        can_cancel: true,
      }),
    );
  };

  const onStartEditPhotograph = (photograph) => {
    setEditingPhotographId(photograph.id);
  };

  const onStopEditPhotograph = () => {
    setEditingPhotographId(null);
  };

  const onActivatePhotograph = (photograph) => {
    if (photograph) {
      setActivePhotographId(photograph.id);
    } else {
      setActivePhotographId(null);
    }
  };

  const onSelectAllPhotographs = () => {
    setSelectedPhotographIds(
      map(gallery.photographs, (photograph) => photograph.id),
    );
  };

  const onUnSelectAllPhotographs = () => {
    setSelectedPhotographIds([]);
  };

  const onSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(union(selectedPhotographIds, [photograph.id]));
  };

  const onUnSelectPhotograph = (photograph) => {
    setSelectedPhotographIds(without(selectedPhotographIds, photograph.id));
  };

  const getIsSelected = (photograph) => {
    return includes(selectedPhotographIds, photograph.id);
  };

  const getIsActivated = (photograph) => {
    return activePhotographId === photograph.id || getIsSelected(photograph);
  };

  const imageRenderer = useCallback(
    function ({ index, left, top, key, photo }) {
      return (
        <GalleryPhotograph
          getIsSelected={getIsSelected}
          getIsActivated={getIsActivated}
          onActivatePhotograph={onActivatePhotograph}
          onSelectPhotograph={onSelectPhotograph}
          onUnSelectPhotograph={onUnSelectPhotograph}
          onDeletePhotograph={onDeletePhotograph}
          onDeletePhotographWithoutGallerySizeFileInfo={
            onDeletePhotographWithoutGallerySizeFileInfo
          }
          onSetAsMapPhotograph={onSetAsMapPhotograph}
          onEditPhotograph={onStartEditPhotograph}
          isMapPhotograph={get(gallery, "map_photograph") === photo.id}
          key={index}
          index={index}
          photo={photo}
          left={left}
          top={top}
          country_id={get(gallery, ["gallery_price", "country"])}
        />
      );
    },
    [
      activePhotographId,
      gallery_id,
      get(gallery, "map_photograph"),
      selectedPhotographIds,
    ],
  );

  const getGallerySpecificActions = () => {
    const actions = [];
    if (size(selectedPhotographIds) > 0) {
      actions.push(
        <div
          className={classes.gallerySpecificAction}
          onClick={onDeleteSelectedPhotographs}
        >
          <DeleteIcon />
          &nbsp;&nbsp; &nbsp;
          <span>
            Delete {size(selectedPhotographIds)} Photograph
            {size(selectedPhotographIds) > 1 ? "s" : ""}
          </span>
        </div>,
      );
      actions.push(
        <div
          className={classes.gallerySpecificAction}
          onClick={onUnSelectAllPhotographs}
        >
          <RadioButtonCheckedIcon />
          &nbsp;&nbsp; &nbsp;
          <span>
            Unselect {size(selectedPhotographIds)} Photograph
            {size(selectedPhotographIds) > 1 ? "s" : ""}
          </span>
        </div>,
      );
    }
    if (size(selectedPhotographIds) == 0) {
      actions.push(
        <div
          className={classes.gallerySpecificAction}
          onClick={onSelectAllPhotographs}
        >
          <RadioButtonUncheckedIcon />
          &nbsp;&nbsp; &nbsp;
          <span>
            Select all {size(gallery.photographs)} photograph
            {size(gallery.photographs) > 1 ? "s" : ""}
          </span>
        </div>,
      );
    }
    return actions;
  };

  const renderEditPhotograph = () => {
    return (
      <PhotographEditor
        photograph_id={editingPhotographId}
        country_id={gallery.gallery_price.country}
        onClose={onStopEditPhotograph}
      />
    );
  };

  return (
    <PageLayout
      title={get(gallery, "gallery_name")}
      gallery={gallery}
      getGallerySpecificActions={getGallerySpecificActions}
    >
      {editingPhotographId && renderEditPhotograph()}
      {loadingGalleries && <LoadingSpinner />}
      <div className={classes.photographs_container}>
        {!loadingGalleries && galleryPhotographs && (
          <>
            <Gallery photos={galleryPhotographs} renderImage={imageRenderer} />
          </>
        )}
        {!loadingGalleries &&
          get(gallery, "id") &&
          size(galleryPhotographs) === 0 && (
            <div>There are no photographs in this gallery</div>
          )}
      </div>
    </PageLayout>
  );
};
